import React, { useEffect, useRef } from "react";
import { signOut } from "aws-amplify/auth";
import { getCurrentUser } from "aws-amplify/auth";

const InactivityTracker: React.FC = () => {
  const timerRef = useRef<NodeJS.Timeout | null>(null); // To store the 1-second interval timeout
  const lastActiveTimeRef = useRef<number>(Date.now()); // To store the last active timestamp
  const hasLogged30Min = useRef<boolean>(false); // To track whether 30-minute log has been printed

  const clearOrUpdateLocalStorage = () => {
    localStorage.setItem("loggedIn", "false");
    localStorage.removeItem("energyType");
    localStorage.removeItem("siteSortModel");
    localStorage.removeItem("sitePaginationModel");
    localStorage.removeItem("projectSortModel");
    localStorage.removeItem("projectPaginationModel");
    localStorage.removeItem("unitSortModel");
    localStorage.removeItem("unitPaginationModel");
    localStorage.removeItem("alertSortModel");
    localStorage.removeItem("alertPaginationModel");
    localStorage.removeItem("userSortModel");
    localStorage.removeItem("userPaginationModel");
    localStorage.removeItem("adminSortModel");
    localStorage.removeItem("adminPaginationModel");
  };

  // Function to check inactivity
  const checkInactivity = async () => {
    const now = Date.now();
    const inactiveTimeInSeconds = (now - lastActiveTimeRef.current) / 1000; // In seconds
    const inactiveTimeInMinutes = inactiveTimeInSeconds / 60; // In minutes

    // if (inactiveTimeInSeconds > 1 && inactiveTimeInSeconds < 1800) {
    //   console.log(`App inactive for: ${inactiveTimeInSeconds.toFixed(3)} seconds`);
    // }

    if (inactiveTimeInMinutes >= 30 && !hasLogged30Min.current) {
      try {
        // Check if there is a current authenticated user
        const user = await getCurrentUser(); // Get the current user
        if (user) {
          // If user is authenticated, sign them out
          signOut();
          clearOrUpdateLocalStorage();
          resetActivity();
          console.log("User signed out due to inactivity.");
        }
      } catch (error) {
        // console.log("No user is currently logged in.");
      }

      hasLogged30Min.current = true; // Ensure we only log once for 30 minutes
    }
  };

  // Function to reset the inactivity timer when activity is detected
  const resetActivity = () => {
    lastActiveTimeRef.current = Date.now(); // Update last active time to now
    hasLogged30Min.current = false; // Reset the 30-minute inactivity flag
  };

  useEffect(() => {
    // Add event listeners for user interactions
    const events = ["mousemove", "keydown", "mousedown", "touchstart"]; // Various events to track

    events.forEach((event) => window.addEventListener(event, resetActivity));

    // Start a timer to check inactivity every second
    timerRef.current = setInterval(() => {
      checkInactivity();
    }, 1000);

    // Clean up the event listeners and interval on component unmount
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      events.forEach((event) => window.removeEventListener(event, resetActivity));
    };
  }, []);

  // for signOut after tab close
  useEffect(() => {
    const handleTabClose = () => {
      // Save timestamp when user leaves the page
      localStorage.setItem("lastUnloadTime", Date.now().toString());
    };

    window.addEventListener("unload", handleTabClose);

    return () => {
      window.removeEventListener("unload", handleTabClose);
    };
  }, []);

  useEffect(() => {
    const lastUnload = localStorage.getItem("lastUnloadTime");
    const isOnStripePaymentGateway = localStorage.getItem("isOnStripePaymentGateway");

    if (lastUnload) {
      const diffInSeconds = (Date.now() - parseInt(lastUnload, 10)) / 1000;

      if (diffInSeconds > 2 && !isOnStripePaymentGateway) {
        // It's not a refresh — assume it's a real tab close
        clearOrUpdateLocalStorage();
        signOut();
        console.log("User signed out due to tab close.");
      }

      // Clean up the timestamp either way
      localStorage.removeItem("lastUnloadTime");
      localStorage.removeItem("isOnStripePaymentGateway");
    }
  }, []);

  return null; // No UI rendering is needed
};

export default InactivityTracker;
